'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Game {
  id: string;
  title: string;
  category: string;
  plays: number;
  likes: number;
  author: string;
  authorAvatar: string;
  thumbnail: string;
  thumbnailAlt: string;
  model: 'Free' | 'Pro' | 'Advanced';
  trending: boolean;
  createdAt: string;
}

export default function GameCard({ game }: { game: Game }) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(game.likes);

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    setLiked(!liked);
    setLikeCount((prev) => liked ? prev - 1 : prev + 1);
  };

  return (
    <Link href="/game-public-page" className="group block rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/30 transition-all duration-200 glow-card">
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <AppImage
          src={game.thumbnail}
          alt={game.thumbnailAlt}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <span className={`
            text-xs font-semibold px-2 py-0.5 rounded-full
            ${game.model === 'Free' ? 'bg-success/20 text-success' :
              game.model === 'Pro'? 'bg-primary/20 text-primary' : 'bg-accent/20 text-accent'}
          `}>
            {game.model}
          </span>
          {game.trending && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-warning/20 text-warning flex items-center gap-1">
              <Icon name="ArrowTrendingUpIcon" size={10} />
              Tendência
            </span>
          )}
        </div>

        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center glow-primary">
            <Icon name="PlayIcon" size={20} className="text-white ml-1" variant="solid" />
          </div>
        </div>

        {/* Category */}
        <div className="absolute bottom-2 left-2">
          <span className="text-xs text-white/80 bg-black/40 px-2 py-0.5 rounded-full">{game.category}</span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-foreground text-sm truncate">{game.title}</h3>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary">{game.authorAvatar[0]}</span>
              </div>
              <p className="text-xs text-muted-foreground truncate">@{game.author}</p>
            </div>
          </div>
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-all duration-150 flex-shrink-0 ${
              liked ? 'bg-danger/15 text-danger' : 'bg-muted/50 text-muted-foreground hover:text-danger hover:bg-danger/10'
            }`}
          >
            <Icon name="HeartIcon" size={13} variant={liked ? 'solid' : 'outline'} />
            <span className="tabular-nums">{likeCount}</span>
          </button>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Icon name="PlayIcon" size={11} />
            <span className="tabular-nums">{game.plays.toLocaleString('pt-PT')}</span>
          </span>
          <span>{game.createdAt}</span>
        </div>
      </div>
    </Link>
  );
}